import React, { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { documentCategories } from 'constants';
import { useRecoilValue } from 'recoil';

import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';
import { useDocumentsCategories } from 'lib/hooks/queries/documents/useDocuments';
import { usePractitioners } from 'lib/hooks/queries/practitioners/usePractitioners';

import { currentPractice } from 'components/practice/practiceState';

import { userState } from '../../state';
import PatientSelectionModal from '../Modal/PatientSelectionModal/PatientSelectionModal';

import InstantPacket from './InstantPacket/InstantPacket';
import List from './List';
import View from './View';
import getDocumentFilters from './lib/defaultFilters';
import documentTableCols from './lib/documentTableCols';
import selectPatient from './lib/handlers/selectPatient';
import useSingleDocument from './lib/useSingleDocument';

export default function Documents({ options, level }) {
  const currentUser = useRecoilValue(userState);

  const { myPortalPractice } = useParams();

  const { patient } = useOutletContext() || {};
  const practice = useRecoilValue(currentPractice);

  const [patientSelection, setPatientSelection] = useState({
    show: false,
    id: null,
    type: null,
    patient: null,
    appointment: null,
    action: null
  });

  const patientId = patient?.id || currentUser?.id;

  const [viewDocument, setViewDocument] = useState(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showInstantPacket, setShowInstantPacket] = useState(false);

  const [sendFaxModal, setSendFaxModal] = useState({ show: false, files: [] });
  const [shareWithPracticeModal, setShareWithPracticeModal] = useState({ show: false, files: [] });

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: categoriesData } = useDocumentsCategories({
    params: {
      patientId,
      staticCategories: documentCategories,
      ...(myPortalPractice && { myPortalPractice })
    },
    dependencies: [myPortalPractice, patientId]
  });

  const categories = categoriesData?.categories || [];

  const _ = useSingleDocument({
    viewDocument,
    setViewDocument,
    categories
  });

  const onSelectPatient = async (patient) =>
    selectPatient({
      patient,
      patientSelection,
      setShowInstantPacket,
      setPatientSelection,
      navigate,
      queryClient
    });

  const handleCloseModal = () => {
    setViewDocument(null);
    setIsEditOpen(false);
  };

  const { data: practitionersData } = usePractitioners(
    myPortalPractice ? { practice_id: myPortalPractice } : null,
    {},
    [myPortalPractice]
  );

  const practitioners = options?.isPatient
    ? [
        {
          f_name: 'Staff',
          l_name: 'Members',
          id: practitionersData?.practitioners?.map((p) => p.id)
        }
      ]
    : practitionersData?.practitioners || [];

  const tableCols = documentTableCols({
    setViewDocument,
    isEditOpen,
    setIsEditOpen,
    patientId,
    navigate,

    practice,
    options,
    setPatientSelection,
    level,
    appointmentId: options?.appointmentId,
    setSendFaxModal,
    setShareWithPracticeModal
  });

  const filters = getDocumentFilters({
    practitioners,
    patient,
    categories,
    level,
    options,
    navigate,
    myPortalPractice
  });

  return (
    <div className="flex h-full flex-col">
      <TableContextProvider
        defaultFilters={filters}
        cols={tableCols}
        name={level + '_documents'}
        pagination>
        <List
          patientId={patientId}
          patient={
            patient || {
              id: currentUser.user_id,
              fullName: currentUser.f_name + ' ' + currentUser.l_name,
              ...currentUser
            }
          }
          appointmentId={options?.appointmentId}
          options={options}
          level={level}
          categories={categories}
          sendFaxModal={sendFaxModal}
          setSendFaxModal={setSendFaxModal}
          setPatientSelection={setPatientSelection}
          setViewDocument={setViewDocument}
          setShowInstantPacket={setShowInstantPacket}
          shareWithPracticeModal={shareWithPracticeModal}
          setShareWithPracticeModal={setShareWithPracticeModal}
        />

        {viewDocument && (
          <View
            viewDocument={viewDocument}
            setViewDocument={setViewDocument}
            handleCloseModal={handleCloseModal}
            action={viewDocument.action}
            patientId={patientId}
            setSendFaxModal={setSendFaxModal}
            patient={patient}
            handle={viewDocument.handle}
            type={viewDocument.type}
            categories={categories}
            options={options}
            appointmentId={options?.appointmentId}
          />
        )}
      </TableContextProvider>

      {patientSelection?.show && options?.enablePatientSelect && (
        <PatientSelectionModal
          isOpen={patientSelection?.show}
          focusOnMount={true}
          onSelect={onSelectPatient}
          isClearable={patientSelection?.type !== 'form'}
          patient={patientSelection?.patient}
          handleClose={() => setPatientSelection({ show: false, patient: null })}
        />
      )}
      {showInstantPacket && (
        <InstantPacket
          setShowInstantPacket={setShowInstantPacket}
          patientId={patientSelection?.id}
        />
      )}
    </div>
  );
}
